import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { MapPinOff } from 'lucide-react';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { SpawnRow } from '../../components/world/SpawnRow';
import { EventChip } from '../../components/world/EventChip';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { MonsterItemDrops } from '../../components/world/MonsterItemDrops';
import { EventPositionList } from '../../components/world/EventPositionList';
import { NpcCard } from '../../components/progression/NpcCard';
import { npcs } from '../../data/guide/npcs';
import { classificationLabel, classifyEntity, monsterKind } from '../../utils/monsterKind';
import { photoIndexNote } from '../../data/guide/monsterPhotoIndexNotes';
import { useEventPositions } from '../../hooks/useEventPositions';
import { positionsFor } from '../../utils/eventPositions';
import { EntityImage } from '../../components/world/EntityImage';
import { imageEntryForMonster } from '../../utils/entityImages';

/** Posiciones del monstruo dentro de eventos (32_event_spawn_rules), separadas de los spots permanentes. */
function MonsterEventPositions({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const ep = useEventPositions();
  const list = ep ? positionsFor(ep.positions, monsterId) : [];
  if (!list.length) return null;
  return <WorldSection id="posiciones-evento" title="Posiciones dentro de eventos" count={list.length} data-id="element-3474" data-mp="1lecds41kedhd">
      <EventPositionList w={w} positions={list} max={12} data-id="element-3475" data-mp="1pgfrkzbphu5t" />
    </WorldSection>;
}
import { BoxContents } from '../../components/world/BagSourceList';
import { monsterBags } from '../../utils/bagSources';
import { mapName, mapPath, formatSeconds, monsterPath } from '../../utils/worldFormat';
import { CanonicalPath } from '../../components/world/CanonicalPath';
import { NpcEntityView } from '../../components/world/NpcEntityView';
import { monsterIdFromPrettyPath } from '../../utils/prettyRoutes';
import { canonicalNpcId } from '../../data/guide/npcIdentity';
import type { Monster, WorldData } from '../../types/world';
export function MonsterDetail() {
  const {
    monsterId,
    slug
  } = useParams();
  const {
    pathname
  } = useLocation();
  useHashScroll();
  return <WorldGate data-id="element-3476" data-mp="i0910i98212o">
      {w => {
      const rawId = slug ? monsterIdFromPrettyPath(pathname) : Number(monsterId);
      // Enlaces antiguos de un NPC unificado (Pet Trainer 234) abren la ficha principal (Trainer 226).
      const id = rawId === undefined ? undefined : canonicalNpcId(rawId);
      const m = id === undefined ? undefined : w.monsters.get(id);
      if (!m) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-3477" data-mp="3qieyb6xkrgv7">
              <p className="text-white" data-id="element-3478" data-mp="3qi5a9ow3bjs5">No encontramos ese monstruo en los archivos.</p>
              <Link to="/guia/mapas?vista=monstruos" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-3479" data-mp="25ytfnqpo3587">Volver a Monstruos</Link>
            </div>;
      }
      return <>
            <CanonicalPath to={monsterPath(m.id)} data-id="element-3480" data-mp="1x1j21ypdn404" />
            {monsterKind(w, m) === 'npc' ? <NpcEntityView w={w} m={m} data-id="element-3481" data-mp="21zkmoo2gbbdp" /> : <MonsterView w={w} m={m} data-id="element-3482" data-mp="21woocymoykoq" />}
          </>;
    }}
    </WorldGate>;
}

/** Procedencia de la foto, separada de la imagen y de los datos de juego. */
function PhotoProvenance({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const img = imageEntryForMonster(w, monsterId)?.image;
  if (!img || img.origin !== 'paquete') return null;
  const family = img.familyReference?.sharedWith ?? [];
  return <Disclosure title="Procedencia de la imagen" data-id="element-3483" data-mp="14bagdwvmu1gs">
      <p className="text-sm text-gray-300" data-id="element-3484" data-mp="149fvgikd29cp">
        Foto de referencia visual{img.packName ? ` (paquete ${img.packName})` : ''}{img.sourceName ? `, rotulada «${img.sourceName}»` : ''}. Retocada para quitar la marca: puede tener
        pequeñas diferencias de textura. No es una captura del cliente Vanguard ni confirma drops, estadísticas ni ubicaciones.
      </p>
      {(img.familyReference?.fromIndex || photoIndexNote(monsterId)?.familyReference) && <p className="mt-2 text-sm text-gray-400" data-id="element-3485" data-mp="1asvresk5xad3">Referencia de familia según el índice original: muestra la familia visual, no la apariencia exacta confirmada en Vanguard.</p>}
      {family.length > 0 && <p className="mt-2 text-sm text-gray-400" data-id="element-3486" data-mp="23gri9gn1w81y">
          Referencia de familia: la misma foto se usa para los ID {family.join(', ')}. Cada ID conserva su propia ficha, mapas, drops y estadísticas.
        </p>}
      <p className="mt-2 text-xs text-gray-500" data-id="element-3487" data-mp="1tibthkbhxavm">{img.matchedBy}</p>
      {photoIndexNote(monsterId)?.sourcePage && <p className="mt-1 text-xs text-gray-500" data-id="element-3488" data-mp="n0oofajxm54p">
          Fuente:{' '}
          <a href={photoIndexNote(monsterId)!.sourcePage} target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-300" data-id="element-3489" data-mp="i2x00qihx159">
            {photoIndexNote(monsterId)!.sourceName ?? photoIndexNote(monsterId)!.sourcePage}
          </a>
          {photoIndexNote(monsterId)!.matchMethod ? ` · ${photoIndexNote(monsterId)!.matchMethod}` : ''}
        </p>}
    </Disclosure>;
}
function MonsterView({
  w,
  m
}: {
  w: WorldData;
  m: Monster;
}) {
  useEffect(() => {
    document.title = `${m.name} · Monstruos · Guía MU Vanguard`;
  }, [m.name]);
  const locations = w.idx.locationsByMonster.get(m.id) ?? [];
  // Solo NPC con identidad confirmada (monsterId); los candidatos de Pet Trainer no reciben su tarjeta.
  const guideNpc = npcs.find(n => n.monsterId === m.id);
  const permanent = locations.filter(l => l.kind === 'ordinary');
  const temporary = locations.filter(l => l.kind !== 'ordinary');
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? [];
  const spots = spawns.filter(s => s.kind === 'spot');
  const invasionSpawns = spawns.filter(s => s.kind === 'invasion');
  const eventIds = Array.from(new Set([...locations, ...spawns].map(x => x.eventId).filter((v): v is string => !!v)));
  return <div data-id="element-3490" data-mp="2uh71r11vqgbu">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Mapas y spots',
      to: '/guia/mapas'
    }, {
      label: 'Monstruos',
      to: '/guia/mapas?vista=monstruos'
    }, {
      label: m.name
    }]} eyebrow={guideNpc ? 'NPC' : classificationLabel[classifyEntity(w, m).cls]} title={m.name} media={imageEntryForMonster(w, m.id)?.image ? <EntityImage entry={imageEntryForMonster(w, m.id)} alt={m.name} name={m.name} size="hero" lazy={false} kind={guideNpc ? 'persona' : 'monstruo'} data-id="element-3492" data-mp="3dd6uie2olnwh" /> : undefined} data-id="element-3491" data-mp="2uhgnudjt03c9">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-3493" data-mp="3n3898f959tyq">
          {m.level !== undefined && <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-sm font-semibold text-[#F3E5AB]" data-id="element-3494" data-mp="4xdes1g6cggh">Nivel del monstruo (catálogo): {m.level}</span>}
          <CopyLinkButton label="Copiar enlace" data-id="element-3495" data-mp="jkc5fu77j6u5" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-3496" data-mp="ojon80j81npv">
        <WorldSection id="ubicaciones" title="Dónde aparece" data-id="element-3497" data-mp="oindbzdxz79q">
          {guideNpc ?
        // NPC de las guías: misma resolución de ubicación que en misiones, recetas y buscador.
        <NpcCard npcId={guideNpc.id} data-id="element-3498" data-mp="fi7c6wko8c81" /> : locations.length === 0 ? <p className="flex items-start gap-2 rounded-2xl border border-white/10 p-4 text-gray-300" data-id="element-3499" data-mp="tucboigq989">
              <MapPinOff className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-3500" data-mp="5lcdodoeebtr" />
              Ubicación no documentada en los archivos disponibles.
            </p> : <div className="grid gap-6 md:grid-cols-2" data-id="element-3501" data-mp="3s05g3seuxo3a">
              {[{
            title: 'Presencia permanente',
            list: permanent
          }, {
            title: 'Solo durante eventos o invasiones',
            list: temporary
          }].map(g => g.list.length > 0 && <div key={g.title} data-id="element-3502" data-mp="3j1tiv7jhjs1v">
                      <h3 className="mb-2 text-sm font-semibold text-gray-400" data-id="element-3503" data-mp="2cunilvnl0gn">{g.title}</h3>
                      <ul className="space-y-2" data-id="element-3504" data-mp="3tinnn8322ytq">
                        {g.list.map(l => <li key={l.id} className="flex flex-wrap items-center gap-2" data-id="element-3505" data-mp="3pgskrz5h8ddu">
                            <Link to={mapPath(l.mapId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-3506" data-mp="3kihaxof6m2vm">
                              {mapName(w, l.mapId)}
                            </Link>
                            {l.eventId && <EventChip w={w} eventId={l.eventId} data-id="element-3507" data-mp="3aqbf23lt71b5" />}
                          </li>)}
                      </ul>
                    </div>)}
            </div>}
        </WorldSection>

        <WorldSection id="spots" title="Spots y áreas" count={spots.length} data-id="element-3508" data-mp="2wrlhkmv2uzm9">
          {spots.length ? <ul className="grid gap-2.5" data-id="element-3509" data-mp="2ruuydqnz7nwm">{spots.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-3510" data-mp="17bhpvukt43h4" />)}</ul> : <p className="text-gray-400" data-id="element-3511" data-mp="17dls88324ufa">No hay spots permanentes configurados para este monstruo.</p>}
        </WorldSection>

        <MonsterEventPositions w={w} monsterId={m.id} data-id="element-3512" data-mp="1h95o4uhmft41" />

        {invasionSpawns.length > 0 && <WorldSection id="invasiones" title="Apariciones en invasiones" count={invasionSpawns.length} data-id="element-3513" data-mp="1lbsjv6hexy6i">
            <p className="mb-3 text-sm text-gray-400" data-id="element-3514" data-mp="olszmerwhb83">Cada fila es un registro. Las filas de mapas alternativos no son monstruos adicionales.</p>
            <ul className="grid gap-2.5" data-id="element-3515" data-mp="tkcmchebir1i">{invasionSpawns.map(s => <SpawnRow key={s.id} w={w} s={s} show="map" data-id="element-3516" data-mp="xlg1t2sgel27" />)}</ul>
          </WorldSection>}

        {eventIds.length > 0 && <WorldSection id="eventos" title="Eventos e invasiones relacionados" data-id="element-3517" data-mp="12dziw9qcnybr">
            <div className="flex flex-wrap gap-2" data-id="element-3518" data-mp="5tqr5qpex18x">{eventIds.map(id => <EventChip key={id} w={w} eventId={id} data-id="element-3519" data-mp="9vvigise1lse" />)}</div>
          </WorldSection>}

        <MonsterItemDrops w={w} monsterId={m.id} data-id="element-3520" data-mp="19j64e4oxk832" />

        <BoxContents w={w} bags={monsterBags(w, m.id)} id="recompensas" title="Recompensas posibles al derrotarlo" intro="Lista interna de resultados posibles al derrotar a este monstruo. No es una caja que abras ni se entregan todos juntos." data-id="element-3521" data-mp="19ieb50it2mfy" />

        <Disclosure title="Estadísticas de catálogo" data-id="element-3522" data-mp="1nifagbjf9ltb">
          <StatList rows={[{
          label: 'Nivel (catálogo)',
          value: m.level
        }, {
          label: 'Vida (catálogo)',
          value: m.hp
        }, {
          label: 'Daño mínimo (catálogo)',
          value: m.damageMin
        }, {
          label: 'Daño máximo (catálogo)',
          value: m.damageMax
        }, {
          label: 'Defensa (catálogo)',
          value: m.defense
        }, {
          label: 'Reaparición (catálogo)',
          value: m.respawnSeconds !== undefined ? formatSeconds(m.respawnSeconds) : undefined
        }, {
          label: 'item_rate_divisor',
          value: m.itemRateDivisor,
          note: 'no es una probabilidad de drop'
        }, {
          label: 'ID del monstruo',
          value: m.id
        }]} data-id="element-3523" data-mp="qt7y9u4p0p22" />
          <p className="mt-3 text-xs text-gray-500" data-id="element-3524" data-mp="3vqtxmcm0pdmj">Valores generales del catálogo. Cada spot puede tener estadísticas configuradas distintas; míralas en la ficha del spot.</p>
        </Disclosure>

        <PhotoProvenance w={w} monsterId={m.id} data-id="element-3525" data-mp="3mq5ptzn7w9i3" />
      </div>
    </div>;
}